import type {
  HeaderAttendee,
  Hold,
  Proposal,
  ReviewItem,
  ReviewQueue,
  Speaker,
  TranscriptRun,
} from '@/lib/api/discovery-review';
import { plural } from '../discovery-logic';

/**
 * Pure helpers behind the review queue and "Who was in the room?" (T-0187,
 * E12-F05; simplified 23 Oct 2026). They word what the API decided:
 * confidence, `preselected` (0.85 or more, Decision 39) and `hold`
 * (`evidenceHold` on the server: rule 8, Decision 53.2) come with each
 * item; the coverage effect comes from `:preview-confirm`. Nothing here
 * counts evidence or sources. Prototype:
 * `docs/discovery/prototype/interviews-simplified.html`.
 */

/** What the selection helpers read of an item in the review queue. */
export type Selectable = Pick<ReviewItem, 'id' | 'hold' | 'preselected'>;

/** Items a consultant can tick: nothing holds them back (`evidenceHold` on the server). */
export const selectable = (p: Selectable) => p.hold === null;

/** Select All, Confident (preselected) or None. */
export function selectionFor(mode: 'all' | 'high' | 'none', proposals: ReadonlyArray<Selectable>) {
  if (mode === 'none') return new Set<string>();
  return new Set(
    proposals.filter((p) => selectable(p) && (mode === 'all' || p.preselected)).map((p) => p.id),
  );
}

/**
 * Keep a selection in step with the proposals as they change: new items and
 * items a speaker mapping just unblocked arrive selected when preselected;
 * blocked, declined and confirmed items leave it. `before` is the blocked
 * state each id had last time.
 */
export function syncSelection(
  picked: ReadonlySet<string>,
  proposals: ReadonlyArray<Selectable>,
  before: ReadonlyMap<string, boolean>,
): Set<string> {
  const next = new Set<string>();
  for (const p of proposals) {
    if (!selectable(p)) continue;
    const wasBlocked = before.get(p.id);
    const fresh = wasBlocked === undefined || wasBlocked;
    if (picked.has(p.id) || (fresh && p.preselected)) next.add(p.id);
  }
  return next;
}

/** Why the AI tagged the passage (E12-F05 §7). */
export function signalText(signal: Proposal['signal']): string {
  const quote = signal.text ? `: “${clip(signal.text, 80)}”` : '';
  switch (signal.kind) {
    case 'signpost':
      return `Spoken signpost${signal.at ? ` at ${signal.at}` : ''}${quote}`;
    case 'on_screen':
      return `Shown on screen${quote}`;
    default:
      return 'Inferred from content';
  }
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** Reading progress in whole percent, from the run's windows; null while it is starting. */
export function readingPercent(run: TranscriptRun | null | undefined): number | null {
  if (!run || !run.window_count) return null;
  return Math.min(100, Math.floor(((run.windows_done ?? 0) / run.window_count) * 100));
}

const SKIP: Record<string, string> = {
  already_decided: 'already decided',
  speaker_unmapped: 'the speaker is not mapped',
  speaker_is_interviewer: 'from the interviewer',
  speaker_unconfirmed: 'who was in the room is not saved yet',
  person_unresolved: 'the person may be a duplicate',
};

/** "2 not confirmed: the speaker is not mapped." */
export function skippedText(skipped: ReadonlyArray<{ reason: string }>): string {
  if (skipped.length === 0) return '';
  const why = [...new Set(skipped.map((s) => SKIP[s.reason] ?? s.reason))].join(', ');
  return ` ${skipped.length} not confirmed: ${why}.`;
}

/** "E-041–E-043" or "E-041" */
export function numberRange(numbers: ReadonlyArray<string>): string {
  if (numbers.length === 0) return '';
  const first = numbers[0]!;
  const last = numbers[numbers.length - 1]!;
  return first === last ? first : `${first}–${last}`;
}

const MIME_BY_EXT: Record<string, string> = {
  vtt: 'text/vtt',
  srt: 'application/x-subrip',
  txt: 'text/plain',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

/** Browsers often leave `.vtt` and `.srt` untyped; name the type from the extension. */
export function transcriptFile(file: File): File {
  if (file.type) return file;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const type = MIME_BY_EXT[ext];
  return type ? new File([file], file.name, { type }) : file;
}

export const TRANSCRIPT_ACCEPT = '.docx,.pdf,.txt,.vtt,.srt';

// ---------------------------------------------------------------------------
// One review queue across interviews and documents (23 Oct 2026)
// ---------------------------------------------------------------------------

/** What the queue shows: everything, one interview, one document, or one area. */
export type ReviewScope =
  | { kind: 'all' }
  | { kind: 'interview'; id: string }
  | { kind: 'document'; id: string }
  | { kind: 'area'; id: string };

/** `all`, an interview id, `doc:<id>` or `area:<id>` (the Sessions address's `review`). */
export function parseScope(value: string | undefined): ReviewScope {
  if (!value || value === 'all') return { kind: 'all' };
  if (value.startsWith('doc:')) return { kind: 'document', id: value.slice(4) };
  if (value.startsWith('area:')) return { kind: 'area', id: value.slice(5) };
  return { kind: 'interview', id: value };
}

export function scopeParam(scope: ReviewScope): string {
  switch (scope.kind) {
    case 'all':
      return 'all';
    case 'document':
      return `doc:${scope.id}`;
    case 'area':
      return `area:${scope.id}`;
    default:
      return scope.id;
  }
}

export function inScope(item: ReviewItem, scope: ReviewScope): boolean {
  switch (scope.kind) {
    case 'all':
      return true;
    case 'interview':
      return item.interview?.id === scope.id;
    case 'document':
      return item.document?.id === scope.id;
    case 'area':
      return item.area_ids.includes(scope.id);
  }
}

/** The Confident filter: the AI confidence band 0.85 and above (Decision 39). */
export const isConfident = (i: Pick<ReviewItem, 'confidence'>) => (i.confidence ?? 0) >= 0.85;

export interface AreaGroup<T> {
  /** Null for items with no area. */
  area_id: string | null;
  label: string;
  items: T[];
}

/**
 * Group items under their FIRST area, in the framework's area order (an
 * item with two areas is listed once, with both chips; decision 3 of the
 * design note). Within a group, the most confident first. Items with no
 * area come last.
 */
export function groupByFirstArea<T extends Pick<ReviewItem, 'area_ids' | 'confidence'>>(
  items: ReadonlyArray<T>,
  areas: ReadonlyArray<{ area_id: string; label: string }>,
): Array<AreaGroup<T>> {
  const known = new Set(areas.map((a) => a.area_id));
  const groups: Array<AreaGroup<T>> = areas.map((a) => ({
    area_id: a.area_id,
    label: a.label,
    items: [],
  }));
  const none: AreaGroup<T> = { area_id: null, label: 'No area', items: [] };
  for (const it of items) {
    const first = it.area_ids.find((a) => known.has(a));
    const g = first ? groups.find((x) => x.area_id === first)! : none;
    g.items.push(it);
  }
  for (const g of [...groups, none]) {
    g.items.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
  }
  return [...groups, none].filter((g) => g.items.length > 0);
}

/**
 * The footer's coverage effect, said once: up to three areas in full
 * ("Budget development: 1 → 2 sources (covered)"); past that, the areas
 * that become covered and how many others gain sources.
 */
export function effectLine(lines: ReadonlyArray<string>): string {
  if (lines.length <= 3) return lines.join(' · ');
  const covered = lines.filter((l) => l.endsWith('(covered)')).map((l) => l.split(':')[0]!);
  const rest = lines.length - covered.length;
  return (
    [
      covered.length ? `Covered after this: ${covered.join(', ')}` : '',
      rest ? `${plural(rest, 'other area')} ${rest === 1 ? 'gains' : 'gain'} sources` : '',
    ]
      .filter(Boolean)
      .join(' · ') + '.'
  );
}

/** "12 items from 3 interviews and 1 document." */
export function queueSummary(items: ReadonlyArray<ReviewItem>): string {
  const interviews = new Set(items.map((i) => i.interview?.id).filter(Boolean)).size;
  const documents = new Set(items.map((i) => i.document?.id).filter(Boolean)).size;
  const from = [
    interviews ? plural(interviews, 'interview') : '',
    documents ? plural(documents, 'document') : '',
  ]
    .filter(Boolean)
    .join(' and ');
  return `${plural(items.length, 'item')}${from ? ` from ${from}` : ''}.`;
}

/** Who said it, the name first: a person, the speaker label while unmatched, or nothing for a document. */
export function whoText(i: ReviewItem): string {
  if (i.document || !i.source) return '';
  if (i.source.kind === 'person') return i.source.name;
  return i.speaker?.label ?? i.source.name;
}

/** The held-back line for one interview, said once (`held_back` from the server). */
export function heldText(h: ReviewQueue['held_back'][number], name: string): string {
  const items = plural(h.count, 'item');
  switch (h.reason) {
    case 'interviewer':
      return `${name}: ${items} ${h.count === 1 ? 'is' : 'are'} from the interviewer, who is never a source.`;
    case 'person_unresolved':
      return `${name}: ${items} ${h.count === 1 ? 'waits' : 'wait'} until a same-person question is answered.`;
    default:
      return `${name}: ${items} ${h.count === 1 ? 'waits' : 'wait'} until its speakers are matched to people.`;
  }
}

export const isSpeakerHold = (h: Hold | null) => h === 'unmapped' || h === 'speaker_unconfirmed';

// ---------------------------------------------------------------------------
// Who was in the room? (speaker matching)
// ---------------------------------------------------------------------------

/**
 * A speaker's pick in the room: `''` nobody yet, `@interviewer`, `p:<id>` a
 * person in People, or `new:<i>` the i-th header attendee who is not in
 * People yet (named on save, after the same-person question when there is one).
 */
export type RoomPick = string;

export const pickOf = (sp: Pick<Speaker, 'is_interviewer' | 'person_id'>): RoomPick =>
  sp.is_interviewer ? '@interviewer' : sp.person_id ? `p:${sp.person_id}` : '';

/** A speaker whose items wait on the room: matched to nobody, or only suggested by the AI. */
export const waitsOnRoom = (sp: Speaker) =>
  sp.proposal_count > 0 && (sp.suggested === true || (!sp.person_id && !sp.is_interviewer));

/** Shown as a row: has items, is suggested, is fixed, or is already matched. */
export const showsInRoom = (sp: Speaker) =>
  sp.proposal_count > 0 || sp.suggested === true || sp.frozen === true;

export function attendeeName(a: HeaderAttendee): string {
  return a.name ?? a.email ?? 'Someone';
}

/**
 * The opening line of the room: what it is for, said once. Fireflies names
 * speakers only "Speaker 1".."Speaker N".
 */
export function roomIntro(speakers: ReadonlyArray<Speaker>, waiting: number): string {
  const generic = speakers.filter((s) => /^speaker\s*\d+$/i.test(s.label.trim())).length;
  const lead =
    generic > 0 && generic === speakers.length
      ? `The transcript names its speakers only as Speaker 1 to ${speakers.length}. Match each one to a person`
      : 'Check who each speaker is';
  const wait = waiting > 0 ? `; ${plural(waiting, 'item waits', 'items wait')} until you do` : '';
  return `${lead}${wait}. Avero’s people are the interviewer: their lines are never a source.`;
}

/** Why a suggestion was made (the reader's basis), said under the select. */
export function suggestionWhy(sp: Speaker): string | null {
  if (sp.suggested) return 'Suggested by AI from the conversation. Saving confirms it.';
  if (sp.match_basis === 'code' && !sp.is_interviewer) return 'Matched by name.';
  return null;
}
